import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  MenuBook as RecipeIcon,
  EditOutlined as EditIcon,
  BlockOutlined as DeactivateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import recipeService from '../../../../services/recipeService';
import type { Recipe } from '../../../../types/venue';

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const RecipeList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/recipes`;

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await recipeService.listRecipes(venueId, { page: page + 1, pageSize });
      setRecipes(result.data);
      setTotal(result.pagination.totalItems);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (recipe: Recipe, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Deactivate recipe "${recipe.name}"? It will no longer appear in active recipe lists.`)) return;
    setDeactivating(recipe._id);
    try {
      await recipeService.deactivateRecipe(venueId, recipe._id);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to deactivate recipe');
    } finally {
      setDeactivating(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RecipeIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Production Recipes</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${base}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Recipe
          </Button>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading production recipes…</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Name', 'Output Item', 'Output Qty', 'Version', 'Updated', 'Status', ''].map(h => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <RecipeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">No production recipes yet. Create one to define what your venue manufactures.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recipes.map(recipe => (
                      <TableRow
                        key={recipe._id}
                        hover
                        sx={{ cursor: 'pointer', opacity: recipe.active ? 1 : 0.55 }}
                        onClick={() => navigate(`${base}/${recipe._id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{recipe.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {recipe.outputInventoryItemId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{recipe.outputQuantity}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">v{recipe.version}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{formatDate(recipe.updatedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={recipe.active ? 'Active' : 'Inactive'}
                            size="small"
                            color={recipe.active ? 'success' : 'default'}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit recipe">
                            <IconButton
                              size="small"
                              onClick={e => { e.stopPropagation(); navigate(`${base}/${recipe._id}`); }}
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {recipe.active && (
                            <Tooltip title="Deactivate recipe">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={e => handleDeactivate(recipe, e)}
                                  disabled={deactivating === recipe._id}
                                  sx={{ color: theme.palette.text.disabled }}
                                >
                                  {deactivating === recipe._id
                                    ? <CircularProgress size={16} />
                                    : <DeactivateIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RecipeList;
